import { useQueryClient } from "@tanstack/react-query";
import { Linking } from "react-native";

import type {
  ZocdocProviderResult,
  ZocdocProviderReviews,
  ZocdocTimeslot,
} from "@/api/generated/models";
import { useUpdateEvent } from "@/api/mutations/conversation";
import { Button, Heading } from "@/components";
import { useCurrentConversationId } from "@/hooks/conversation";
import { updateFieldsForEvent } from "@/hooks/useInfiniteMessages";
import { useZocdocBookingStore } from "@/store/zocdocBooking";
import { formatDayHeading, formatTime, todayYMD } from "@/utils/datetime";
import { PhaseContainer } from "./PhaseContainer";
import { ProviderCard } from "./ProvidersPhase";

type ConfirmPhaseProps = {
  selectedProvider: ZocdocProviderResult;
  carrierName: string | null;
  reviews: ZocdocProviderReviews | null;
  timeslot: ZocdocTimeslot;
  eventId: string;
  closeModal: () => void;
  onBack: () => void;
};

export const ConfirmPhase = ({
  selectedProvider,
  carrierName,
  reviews,
  timeslot,
  eventId,
  closeModal,
  onBack,
}: ConfirmPhaseProps) => {
  const today = todayYMD();
  const selectedDate = timeslot.start_time.slice(0, 10);
  const { mutate: updateEvent } = useUpdateEvent();
  const queryClient = useQueryClient();
  const conversationId = useCurrentConversationId();
  const bookingContent = useZocdocBookingStore((s) => s.bookingContent);
  const handleConfirm = () => {
    if (!timeslot.booking_url || !bookingContent) return;
    Linking.openURL(timeslot.booking_url);
    updateFieldsForEvent(queryClient, conversationId!, eventId, {
      content: { ...bookingContent, final: true },
    });
    updateEvent({ eventId, data: { mark_final: true } });
    closeModal();
  };

  return (
    <PhaseContainer
      title="Everything look good?"
      currentStep={6}
      onBack={onBack}
    >
      <ProviderCard
        provider={selectedProvider}
        carrierName={carrierName}
        reviews={reviews}
      />
      <Heading size="$md">
        {formatDayHeading(selectedDate, today)} at{" "}
        {formatTime(timeslot.start_time)}
      </Heading>
      <Button
        mode="primary"
        alignSelf="flex-end"
        dataTrackingId="zocdoc-finish-booking"
        disabled={!timeslot.booking_url}
        onPress={handleConfirm}
      >
        Finish booking on Zocdoc
      </Button>
    </PhaseContainer>
  );
};
